<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import getWootAxios from 'dashboard/helper/getWootAxios';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
  isSavingAll: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const GUIDANCE_SEED_QUERY_KEYS = [
  'guidance_seed',
  'guidance_seed_source',
  'guidance_seed_editor',
  'guidance_seed_reason',
  'guidance_seed_query',
];
const GUIDANCE_EDITOR_IDS = [
  'guardrails',
  'response_guidelines',
  'communication_style',
  'runtime_assembly',
  'scenarios',
];
const isSubmitting = ref(false);
const activeEditor = ref('guardrails');
const guardrailsText = ref('');
const responseGuidelinesText = ref('');
const responseStyle = ref('adaptive');
const malayReplyForm = ref('auto');
const thaiReplyForm = ref('auto');
const msShortFormGlossary = ref([]);
const thChatGlossary = ref([]);
const overviewText = ref('');
const productHighlightsText = ref('');
const jobscopeText = ref('');
const handlingFlowText = ref('');
const answerLengthStrategy = ref('balanced');
const answerDirectnessStrategy = ref('answer_first');
const sensitiveTopicStrategy = ref('conservative');
const toneEnforcementStrategy = ref('balanced');
const memoryDepthStrategy = ref('standard');
const maxResponseSegments = ref(1);
const answerConfidenceStrategy = ref('off');
const citationMode = ref('off');
const outsideKnowledgeStrategy = ref('standard');
const promiseSafetyStrategy = ref('standard');
const showScenarioModal = ref(false);
const editingScenarioId = ref(null);
const isDirty = ref(false);
const isSyncingFromStore = ref(false);
const lastSavedSnapshot = ref('');
const lastScenarioDraftSnapshot = ref('');
const scenarios = reactive([]);
const scenarioForm = reactive({
  title: '',
  description: '',
  instruction: '',
  enabled: true,
});

const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const aiCloserUIFlags = computed(() => store.getters['aiCloser/getUIFlags'] || {});
const isLoadingConfig = computed(() => aiCloserUIFlags.value.isFetchingConfig);
const guidanceMetricsLoading = ref(false);
const guidanceCardMetrics = ref({});
const guidanceScenarioMetrics = ref({});

const GUIDANCE_METRIC_CARD_KEYS = [
  'guardrails',
  'communication_style',
  'response_guidelines',
  'runtime_assembly',
  'scenarios',
  'runtime_identity_inputs',
];
const GUIDANCE_METRIC_WINDOW_DAYS = 30;

const normalizeLines = text =>
  (text || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);

const safeText = value => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const slugScenarioTitle = value =>
  safeText(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'scenario';

const buildScenarioAttributionKey = (scenario, index) => {
  const rawId = safeText(scenario?.id).trim();
  if (rawId) return rawId;
  return `legacy:${slugScenarioTitle(scenario?.title)}:${index + 1}`;
};

const emptyGuidanceCardMetric = () => ({
  used: 0,
  resolved: 0,
  escalated: 0,
  resolvedRate: 0,
  escalatedRate: 0,
});

const blankGuidanceCardMetrics = () =>
  GUIDANCE_METRIC_CARD_KEYS.reduce((acc, key) => {
    acc[key] = emptyGuidanceCardMetric();
    return acc;
  }, {});

const normalizeGuidanceOutcomeRows = response =>
  (response?.data?.data || []).reduce((acc, row) => {
    const key = safeText(row?.key).trim();
    if (!key) return acc;
    const used = Number(row?.used_count) || 0;
    const resolved = Number(row?.resolved_count) || 0;
    const escalated = Number(row?.escalated_count) || 0;
    acc[key] = {
      used,
      resolved,
      escalated,
      resolvedRate: used > 0 ? resolved / used : 0,
      escalatedRate: used > 0 ? escalated / used : 0,
    };
    return acc;
  }, {});

const identityLocaleSummary = computed(() => ({
  country: safeText(aiCloserConfig.value?.country || 'Malaysia'),
  preferredLanguage: safeText(aiCloserConfig.value?.preferred_language || 'English'),
}));
const runtimeIdentitySummary = computed(() => ({
  nickname:
    safeText(aiCloserConfig.value?.nickname) ||
    safeText(props.assistant?.name) ||
    'Not set',
  age: safeText(aiCloserConfig.value?.age) || 'Not set',
  gender: safeText(aiCloserConfig.value?.gender) || 'neutral',
  country: identityLocaleSummary.value.country,
  preferredLanguage: identityLocaleSummary.value.preferredLanguage,
  aiVoice: safeText(aiCloserConfig.value?.ai_voice) || 'Not set',
}));
const runtimeIdentityFieldCount = computed(
  () =>
    [
      runtimeIdentitySummary.value.nickname,
      runtimeIdentitySummary.value.age,
      runtimeIdentitySummary.value.gender,
      runtimeIdentitySummary.value.country,
      runtimeIdentitySummary.value.preferredLanguage,
      runtimeIdentitySummary.value.aiVoice,
    ].filter(value => safeText(value).trim() && safeText(value).trim() !== 'Not set').length
);

const RESPONSE_STYLE_OPTIONS = [
  { value: 'adaptive', label: 'Adaptive' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];

const ANSWER_LENGTH_OPTIONS = [
  { value: 'concise', label: 'Concise' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'detailed', label: 'Detailed' },
];

const ANSWER_DIRECTNESS_OPTIONS = [
  { value: 'answer_first', label: 'Answer first' },
  { value: 'clarify_first', label: 'Clarify first' },
  { value: 'guided', label: 'Guide step by step' },
];

const SENSITIVE_TOPIC_OPTIONS = [
  { value: 'conservative', label: 'Conservative' },
  { value: 'standard', label: 'Standard' },
];

const TONE_ENFORCEMENT_OPTIONS = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'strict', label: 'Strict' },
];

const MEMORY_DEPTH_OPTIONS = [
  { value: 'recent', label: 'Recent turns only' },
  { value: 'standard', label: 'Standard context window' },
  { value: 'deep', label: 'Deep conversation memory' },
];

const MAX_RESPONSE_SEGMENT_OPTIONS = [
  { value: 1, label: '1 bubble' },
  { value: 2, label: '2 bubbles' },
  { value: 3, label: '3 bubbles' },
  { value: 4, label: '4 bubbles' },
  { value: 5, label: '5 bubbles' },
  { value: 6, label: '6 bubbles' },
];

const ANSWER_CONFIDENCE_OPTIONS = [
  { value: 'off', label: 'Do not gate auto-replies' },
  { value: 'standard', label: 'Suggest when confidence looks weak' },
  { value: 'strict', label: 'Only auto-reply when confidence looks strong' },
];

const CITATION_MODE_OPTIONS = [
  { value: 'off', label: 'Do not add citations' },
  { value: 'auto', label: 'Add citations when sources are used' },
];

const STRUCTURED_GUIDELINE_PREFIX = '[AI Strategy:';
const STRUCTURED_GUARDRAIL_PREFIX = '[AI Guardrail:';
const STRUCTURED_GUIDELINE_MAP = {
  answer_length: {
    defaultValue: 'balanced',
    options: {
      concise:
        'Keep answers concise unless the customer asks for more detail.',
      balanced:
        'Start with the direct answer, then add only the most useful supporting detail.',
      detailed:
        'Give fuller step-by-step answers when the customer needs more explanation.',
    },
  },
  directness: {
    defaultValue: 'answer_first',
    options: {
      answer_first:
        'Lead with the direct answer first, then explain the next step or reasoning.',
      clarify_first:
        'Ask a clarifying question before answering when the request is ambiguous.',
      guided:
        'Lead with the next best action and guide the customer step by step.',
    },
  },
  sensitive_topics: {
    defaultValue: 'conservative',
    options: {
      conservative:
        'Use conservative wording for refunds, compliance, payments, legal, and account-risk topics. Avoid over-promising.',
      standard:
        'Handle sensitive topics normally, but still avoid guarantees and escalate when rules require it.',
    },
  },
  tone_enforcement: {
    defaultValue: 'balanced',
    options: {
      flexible:
        'Keep the saved voice as a guide, but allow lighter adaptation to the customer when it improves rapport.',
      balanced:
        'Keep the saved voice consistent, while still adapting slightly to the customer and situation.',
      strict:
        'Stay tightly aligned to the saved voice and avoid drifting into extra hype, slang, or unsupported tone shifts.',
    },
  },
};

const OUTSIDE_KNOWLEDGE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'clarify', label: 'Clarify or guide safely' },
  { value: 'escalate', label: 'Escalate instead of guessing' },
];

const PROMISE_SAFETY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'strict', label: 'Never promise unsupported outcomes' },
];

const pickAllowedStrategy = (value, allowed, fallback) => {
  const candidate = safeText(value).trim();
  return allowed.includes(candidate) ? candidate : fallback;
};

const normalizePositiveSegmentCount = value => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(6, Math.max(1, parsed));
};

const STRUCTURED_GUARDRAIL_MAP = {
  outside_knowledge: {
    defaultValue: 'standard',
    options: {
      clarify:
        'When configured knowledge is missing or ambiguous, ask a clarifying question or guide the customer to a safer next step instead of guessing.',
      escalate:
        'When configured knowledge is missing or ambiguous, escalate rather than inventing an answer.',
    },
  },
  promise_safety: {
    defaultValue: 'standard',
    options: {
      strict:
        'Never promise approvals, refunds, timelines, winnings, or policy outcomes unless the configured knowledge explicitly confirms them.',
    },
  },
};

const MALAY_SHORT_FORM_GLOSSARY = [
  { short: 'x', meaning: 'tak', note: 'standalone common MY slang' },
  { short: 'xnak', meaning: 'tak nak', note: 'do not want' },
  { short: 'xboleh', meaning: 'tak boleh', note: 'cannot / not allowed' },
  { short: 'xde', meaning: 'tak ada', note: 'not available / do not have' },
  { short: 'sy', meaning: 'saya', note: 'I / me' },
  { short: 'awk', meaning: 'awak', note: 'you' },
  { short: 'org', meaning: 'orang', note: 'person / people' },
  { short: 'nk', meaning: 'nak', note: 'want' },
  { short: 'sbb', meaning: 'sebab', note: 'because' },
  { short: 'wd', meaning: 'withdraw', note: 'withdrawal' },
  { short: 'dep', meaning: 'deposit', note: 'deposit / top up' },
];

const THAI_CHAT_GLOSSARY = [
  { short: 'ค้าบ', meaning: 'ครับ', note: 'chat male polite particle' },
  { short: 'ค่า', meaning: 'ค่ะ', note: 'chat female polite particle' },
  { short: 'น้า', meaning: 'นะ', note: 'softener / friendly tone' },
  { short: 'แป๊บ', meaning: 'แป๊บเดียว', note: 'a moment' },
  { short: 'มั้ย', meaning: 'ไหม', note: 'informal particle' },
  { short: 'งับ', meaning: 'ครับ/ค่ะ', note: 'cute tone, use lightly' },
  { short: '555', meaning: 'ฮ่าๆ', note: 'Thai laughter' },
];

const defaultMalayGlossary = () =>
  MALAY_SHORT_FORM_GLOSSARY.map(row => ({ ...row }));

const defaultThaiChatGlossary = () =>
  THAI_CHAT_GLOSSARY.map(row => ({ ...row }));

const normalizeGlossaryRows = rows => {
  const list = Array.isArray(rows) ? rows : [];
  const cleaned = list
    .map(row => ({
      short: safeText(row?.short).trim(),
      meaning: safeText(row?.meaning).trim(),
      note: safeText(row?.note).trim(),
    }))
    .filter(row => row.short && row.meaning);
  const seen = new Set();
  return cleaned.filter(row => {
    const key = row.short.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const featureCitationEnabled = value =>
  value === true || value === 'true' || value === 1 || value === '1';

const buildStructuredGuideline = (key, value) => {
  const definition = STRUCTURED_GUIDELINE_MAP[key];
  if (!definition || value === definition.defaultValue) return '';
  const copy = definition?.options?.[value];
  return copy ? `${STRUCTURED_GUIDELINE_PREFIX}${key}] ${copy}` : '';
};

const buildStructuredGuardrail = (key, value) => {
  const definition = STRUCTURED_GUARDRAIL_MAP[key];
  if (!definition || value === definition.defaultValue) return '';
  const copy = definition?.options?.[value];
  return copy ? `${STRUCTURED_GUARDRAIL_PREFIX}${key}] ${copy}` : '';
};

const parseStructuredGuidelines = rows => {
  const next = {
    answer_length: STRUCTURED_GUIDELINE_MAP.answer_length.defaultValue,
    directness: STRUCTURED_GUIDELINE_MAP.directness.defaultValue,
    sensitive_topics: STRUCTURED_GUIDELINE_MAP.sensitive_topics.defaultValue,
    remaining: [],
  };

  (Array.isArray(rows) ? rows : []).forEach(rawLine => {
    const line = safeText(rawLine).trim();
    if (!line) return;

    const match = line.match(/^\[AI Strategy:([a-z_]+)\]\s*(.*)$/);
    if (!match) {
      next.remaining.push(line);
      return;
    }

    const [, key, body] = match;
    const definition = STRUCTURED_GUIDELINE_MAP[key];
    if (!definition) {
      next.remaining.push(line);
      return;
    }

    const matchedOption = Object.entries(definition.options).find(
      ([, optionCopy]) => optionCopy === body.trim()
    );
    if (!matchedOption) {
      next.remaining.push(line);
      return;
    }

    next[key] = matchedOption[0];
  });

  return next;
};

const parseStructuredGuardrails = rows => {
  const next = {
    outside_knowledge: STRUCTURED_GUARDRAIL_MAP.outside_knowledge.defaultValue,
    promise_safety: STRUCTURED_GUARDRAIL_MAP.promise_safety.defaultValue,
    remaining: [],
  };

  (Array.isArray(rows) ? rows : []).forEach(rawLine => {
    const line = safeText(rawLine).trim();
    if (!line) return;

    const match = line.match(/^\[AI Guardrail:([a-z_]+)\]\s*(.*)$/);
    if (!match) {
      next.remaining.push(line);
      return;
    }

    const [, key, body] = match;
    const definition = STRUCTURED_GUARDRAIL_MAP[key];
    if (!definition) {
      next.remaining.push(line);
      return;
    }

    const matchedOption = Object.entries(definition.options).find(
      ([, optionCopy]) => optionCopy === body.trim()
    );
    if (!matchedOption) {
      next.remaining.push(line);
      return;
    }

    next[key] = matchedOption[0];
  });

  return next;
};

const generateId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `guidance_scenario_${Date.now()}_${Math.random()
      .toString(16)
      .slice(2)}`;
  }
};

const normalizeScenario = (raw, index) => {
  const item = raw || {};
  return {
    id: item.id || generateId(),
    attributionKey: buildScenarioAttributionKey(item, index),
    title: (item.title || '').toString(),
    description: (item.description || '').toString(),
    instruction: (item.instruction || '').toString(),
    enabled: item.enabled !== false,
    priority: Number.isFinite(Number(item.priority))
      ? Number(item.priority)
      : index + 1,
  };
};

const structuredGuardrails = computed(() =>
  [
    buildStructuredGuardrail('outside_knowledge', outsideKnowledgeStrategy.value),
    buildStructuredGuardrail('promise_safety', promiseSafetyStrategy.value),
  ].filter(Boolean)
);
const persistedGuardrails = computed(() => [
  ...structuredGuardrails.value,
  ...normalizeLines(guardrailsText.value),
]);
const guardrailsCount = computed(() => persistedGuardrails.value.length);
const structuredResponseGuidelines = computed(() =>
  [
    buildStructuredGuideline('answer_length', answerLengthStrategy.value),
    buildStructuredGuideline('directness', answerDirectnessStrategy.value),
    buildStructuredGuideline('sensitive_topics', sensitiveTopicStrategy.value),
  ].filter(Boolean)
);
const persistedResponseGuidelines = computed(() => [
  ...structuredResponseGuidelines.value,
  ...normalizeLines(responseGuidelinesText.value),
]);
const responseGuidelinesCount = computed(
  () => persistedResponseGuidelines.value.length
);
const communicationStyleCount = computed(() => {
  let count = 1;
  if (malayReplyForm.value && malayReplyForm.value !== 'auto') count += 1;
  if (thaiReplyForm.value && thaiReplyForm.value !== 'auto') count += 1;
  count += normalizeGlossaryRows(msShortFormGlossary.value).length;
  count += normalizeGlossaryRows(thChatGlossary.value).length;
  return count;
});
const runtimeAssemblyCount = computed(
  () =>
    [
      overviewText.value,
      productHighlightsText.value,
      jobscopeText.value,
      handlingFlowText.value,
    ].filter(value => safeText(value).trim()).length
);
const scenariosCount = computed(() => scenarios.length);
const guidanceMetricsWindowLabel = computed(
  () => `Last ${GUIDANCE_METRIC_WINDOW_DAYS} days`
);

const guidanceSnapshot = () =>
  JSON.stringify({
    guardrails: persistedGuardrails.value,
    response_guidelines: persistedResponseGuidelines.value,
    answer_generation: {
      answer_length_strategy: answerLengthStrategy.value || 'balanced',
      directness_strategy: answerDirectnessStrategy.value || 'answer_first',
      sensitive_topics_strategy: sensitiveTopicStrategy.value || 'conservative',
      tone_enforcement_strategy: toneEnforcementStrategy.value || 'balanced',
      memory_depth_strategy: memoryDepthStrategy.value || 'standard',
      max_response_segments: normalizePositiveSegmentCount(maxResponseSegments.value),
      answer_confidence_strategy: answerConfidenceStrategy.value || 'off',
      outside_knowledge_strategy: outsideKnowledgeStrategy.value || 'standard',
      promise_safety_strategy: promiseSafetyStrategy.value || 'standard',
    },
    answer_strategy: {
      answer_length: answerLengthStrategy.value || 'balanced',
      directness: answerDirectnessStrategy.value || 'answer_first',
      sensitive_topics: sensitiveTopicStrategy.value || 'conservative',
      citation_mode: citationMode.value || 'off',
    },
    guardrail_strategy: {
      outside_knowledge: outsideKnowledgeStrategy.value || 'standard',
      promise_safety: promiseSafetyStrategy.value || 'standard',
    },
    response_style: responseStyle.value || 'adaptive',
    language_style: {
      ms_reply_form: malayReplyForm.value || 'auto',
      th_reply_form: thaiReplyForm.value || 'auto',
      ms_short_form_glossary: normalizeGlossaryRows(msShortFormGlossary.value),
      th_chat_glossary: normalizeGlossaryRows(thChatGlossary.value),
    },
    overview: safeText(overviewText.value),
    product_highlights: safeText(productHighlightsText.value),
    jobscope: safeText(jobscopeText.value),
    handling_flow: safeText(handlingFlowText.value),
    scenarios: scenarios.map((scenario, idx) => ({
      id: scenario.id || '',
      title: safeText(scenario.title).trim(),
      description: safeText(scenario.description).trim(),
      instruction: safeText(scenario.instruction).trim(),
      enabled: scenario.enabled !== false,
      priority: idx + 1,
    })),
  });

const scenarioDraftSnapshot = () =>
  JSON.stringify({
    title: safeText(scenarioForm.title).trim(),
    description: safeText(scenarioForm.description).trim(),
    instruction: safeText(scenarioForm.instruction).trim(),
    enabled: scenarioForm.enabled !== false,
    editingScenarioId: editingScenarioId.value || null,
  });

const markSavedSnapshot = () => {
  lastSavedSnapshot.value = guidanceSnapshot();
  isDirty.value = false;
};

const markScenarioDraftSnapshot = () => {
  lastScenarioDraftSnapshot.value = scenarioDraftSnapshot();
};

const refreshDirtyState = () => {
  if (isSyncingFromStore.value) return;
  if (!lastSavedSnapshot.value) return;
  isDirty.value = guidanceSnapshot() !== lastSavedSnapshot.value;
};

const canSaveScenarioDraft = computed(
  () =>
    Boolean(
      props.assistant?.id &&
      showScenarioModal.value &&
        !isSubmitting.value &&
        !isLoadingConfig.value &&
        safeText(scenarioForm.title).trim() &&
        safeText(scenarioForm.instruction).trim() &&
        scenarioDraftSnapshot() !== lastScenarioDraftSnapshot.value
    )
);

const canPersistGuidance = computed(
  () =>
    Boolean(
      props.assistant?.id &&
        !isLoadingConfig.value &&
        !isSubmitting.value &&
        isDirty.value
    )
);

const canSave = computed(
  () => canSaveScenarioDraft.value || canPersistGuidance.value
);

const guidanceMetricForCard = cardKey =>
  guidanceCardMetrics.value?.[cardKey] || emptyGuidanceCardMetric();

const guidanceMetricForScenario = scenario =>
  guidanceScenarioMetrics.value?.[scenario?.attributionKey] || emptyGuidanceCardMetric();

const guidanceMetricForKey = key =>
  guidanceScenarioMetrics.value?.[key] ||
  guidanceCardMetrics.value?.[key] ||
  emptyGuidanceCardMetric();

const formatMetricRate = value => `${Math.round((Number(value) || 0) * 100)}%`;

const guidanceMetricPills = cardKey => {
  const metrics = guidanceMetricForKey(cardKey);
  return [
    { label: 'Used', value: metrics.used },
    { label: 'Resolved', value: metrics.resolved },
    { label: 'Escalated', value: metrics.escalated },
  ];
};

const guidanceMetricRateCopy = cardKey => {
  const metrics = guidanceMetricForKey(cardKey);
  return `Resolve rate ${formatMetricRate(metrics.resolvedRate)} • Escalation rate ${formatMetricRate(metrics.escalatedRate)}`;
};

const guidanceOverviewCards = computed(() => [
  {
    label: 'Do-not-do rules',
    value: guardrailsCount.value,
    note: 'Safety, compliance, and escalation boundaries.',
  },
  {
    label: 'Reply instructions',
    value: responseGuidelinesCount.value,
    note: 'Tone, structure, and response quality rules.',
  },
  {
    label: 'Scenarios',
    value: scenariosCount.value,
    note: 'Reusable guidance for recurring situations.',
  },
  {
    label: 'Business context',
    value: runtimeAssemblyCount.value,
    note: 'Brand overview, highlights, role, and handling flow.',
  },
]);

const advancedCommunicationStyleCount = computed(() => {
  let count = 0;
  if (malayReplyForm.value && malayReplyForm.value !== 'auto') count += 1;
  if (thaiReplyForm.value && thaiReplyForm.value !== 'auto') count += 1;
  count += normalizeGlossaryRows(msShortFormGlossary.value).length;
  count += normalizeGlossaryRows(thChatGlossary.value).length;
  return count;
});

const guidanceCards = computed(() => [
  {
    id: 'guardrails',
    title: 'Guardrails',
    description: 'Hard rules, compliance boundaries, and unsafe answer blocks.',
    count: guardrailsCount.value,
    status: guardrailsCount.value ? 'Active' : 'Empty',
    icon: 'i-lucide-shield-check',
    type: 'Guardrails',
    metrics: guidanceMetricForCard('guardrails'),
  },
  {
    id: 'communication_style',
    title: 'Reply style',
    description: 'Main tone plus optional Malay and Thai language controls.',
    count: communicationStyleCount.value,
    status: communicationStyleCount.value ? 'Active' : 'Empty',
    icon: 'i-lucide-messages-square',
    type: 'Style',
    metrics: guidanceMetricForCard('communication_style'),
  },
  {
    id: 'response_guidelines',
    title: 'Reply instructions',
    description: 'How answers should be structured, phrased, and kept useful.',
    count: responseGuidelinesCount.value,
    status: responseGuidelinesCount.value ? 'Active' : 'Empty',
    icon: 'i-lucide-pen-line',
    type: 'Guidelines',
    metrics: guidanceMetricForCard('response_guidelines'),
  },
  {
    id: 'runtime_assembly',
    title: 'Business context',
    description: 'Long-form context for brand overview, highlights, role, and flow.',
    count: runtimeAssemblyCount.value,
    status: runtimeAssemblyCount.value ? 'Active' : 'Empty',
    icon: 'i-lucide-file-text',
    type: 'Assembly',
    metrics: guidanceMetricForCard('runtime_assembly'),
  },
  {
    id: 'scenarios',
    title: 'Scenarios',
    description: 'Contextual instructions for recurring if/then situations.',
    count: scenariosCount.value,
    status: scenariosCount.value ? 'Active' : 'Empty',
    icon: 'i-lucide-sparkles',
    type: 'Scenario',
    metrics: guidanceMetricForCard('scenarios'),
  },
]);

const guidanceItems = computed(() => {
  const items = [
    {
      id: 'guardrails',
      title: 'Guardrails',
      type: 'Guardrails',
      status: guardrailsCount.value ? 'Active' : 'Empty',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForCard('guardrails'),
    },
    {
      id: 'communication_style',
      title: 'Reply style',
      type: 'Style',
      status: communicationStyleCount.value ? 'Active' : 'Empty',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForCard('communication_style'),
    },
    {
      id: 'response_guidelines',
      title: 'Reply instructions',
      type: 'Guidelines',
      status: responseGuidelinesCount.value ? 'Active' : 'Empty',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForCard('response_guidelines'),
    },
    {
      id: 'runtime_assembly',
      title: 'Business context',
      type: 'Assembly',
      status: runtimeAssemblyCount.value ? 'Active' : 'Empty',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForCard('runtime_assembly'),
    },
    {
      id: 'scenarios',
      title: 'Scenarios (section)',
      type: 'Scenario',
      status: scenariosCount.value ? 'Active' : 'Empty',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForCard('scenarios'),
    },
    {
      id: 'runtime_identity_inputs',
      title: 'Runtime identity inputs',
      type: 'Identity',
      status: runtimeIdentityFieldCount.value ? 'Active' : 'Needs review',
      lastUpdated: 'Settings owner',
      metrics: guidanceMetricForCard('runtime_identity_inputs'),
    },
  ];

  scenarios.forEach(s => {
    items.push({
      id: s.id,
      metricKey: s.attributionKey,
      title: s.title || 'Untitled scenario',
      type: 'Scenario',
      status: s.enabled !== false ? 'Active' : 'Inactive',
      lastUpdated: 'Runtime owner',
      metrics: guidanceMetricForScenario(s),
    });
  });

  return items;
});

const loadFromConfig = config => {
  const ps = config?.prompt_sections || {};
  const rawGuardrails = Array.isArray(ps.guardrails) ? ps.guardrails : [];
  const parsedGuardrails = parseStructuredGuardrails(rawGuardrails);
  const answerGeneration =
    ps.answer_generation && typeof ps.answer_generation === 'object'
      ? ps.answer_generation
      : {};
  const guidelines = Array.isArray(ps.response_guidelines)
    ? ps.response_guidelines
    : [];
  const parsedGuidelines = parseStructuredGuidelines(guidelines);
  const savedScenarios = Array.isArray(ps.scenarios) ? ps.scenarios : [];
  const languageStyle = ps.language_style || {};
  const retrievalConfig =
    ps.knowledge_retrieval && typeof ps.knowledge_retrieval === 'object'
      ? ps.knowledge_retrieval
      : {};

  isSyncingFromStore.value = true;
  try {
    guardrailsText.value = parsedGuardrails.remaining.join('\n');
    outsideKnowledgeStrategy.value = pickAllowedStrategy(
      answerGeneration.outside_knowledge_strategy,
      OUTSIDE_KNOWLEDGE_OPTIONS.map(option => option.value),
      parsedGuardrails.outside_knowledge
    );
    promiseSafetyStrategy.value = pickAllowedStrategy(
      answerGeneration.promise_safety_strategy,
      PROMISE_SAFETY_OPTIONS.map(option => option.value),
      parsedGuardrails.promise_safety
    );
    responseGuidelinesText.value = parsedGuidelines.remaining.join('\n');
    answerLengthStrategy.value = pickAllowedStrategy(
      answerGeneration.answer_length_strategy,
      ANSWER_LENGTH_OPTIONS.map(option => option.value),
      parsedGuidelines.answer_length
    );
    answerDirectnessStrategy.value = pickAllowedStrategy(
      answerGeneration.directness_strategy,
      ANSWER_DIRECTNESS_OPTIONS.map(option => option.value),
      parsedGuidelines.directness
    );
    sensitiveTopicStrategy.value = pickAllowedStrategy(
      answerGeneration.sensitive_topics_strategy,
      SENSITIVE_TOPIC_OPTIONS.map(option => option.value),
      parsedGuidelines.sensitive_topics
    );
    toneEnforcementStrategy.value = pickAllowedStrategy(
      answerGeneration.tone_enforcement_strategy,
      TONE_ENFORCEMENT_OPTIONS.map(option => option.value),
      'balanced'
    );
    memoryDepthStrategy.value = pickAllowedStrategy(
      answerGeneration.memory_depth_strategy,
      MEMORY_DEPTH_OPTIONS.map(option => option.value),
      'standard'
    );
    maxResponseSegments.value = normalizePositiveSegmentCount(
      answerGeneration.max_response_segments
    );
    answerConfidenceStrategy.value = pickAllowedStrategy(
      answerGeneration.answer_confidence_strategy,
      ANSWER_CONFIDENCE_OPTIONS.map(option => option.value),
      'off'
    );
    citationMode.value =
      ['auto', 'always'].includes(String(retrievalConfig.citation_mode))
        ? 'auto'
        : featureCitationEnabled(props.assistant?.config?.feature_citation)
          ? 'auto'
          : 'off';
    responseStyle.value = safeText(config?.response_style || 'adaptive') || 'adaptive';
    malayReplyForm.value = safeText(languageStyle.ms_reply_form || 'auto') || 'auto';
    thaiReplyForm.value = safeText(languageStyle.th_reply_form || 'auto') || 'auto';
    msShortFormGlossary.value = normalizeGlossaryRows(
      languageStyle.ms_short_form_glossary
    );
    thChatGlossary.value = normalizeGlossaryRows(
      languageStyle.th_chat_glossary
    );
    overviewText.value = safeText(ps.overview);
    productHighlightsText.value = safeText(ps.product_highlights);
    jobscopeText.value = safeText(ps.jobscope);
    handlingFlowText.value = safeText(ps.handling_flow);

    scenarios.length = 0;
    savedScenarios.forEach((s, idx) => {
      scenarios.push(normalizeScenario(s, idx));
    });
    markSavedSnapshot();
  } finally {
    isSyncingFromStore.value = false;
  }
};

const openEditor = id => {
  activeEditor.value = id;
};

const guidanceSeedContext = computed(() => {
  const source = safeText(route.query?.guidance_seed_source).trim();
  const editor = safeText(route.query?.guidance_seed_editor).trim();
  const reason = safeText(route.query?.guidance_seed_reason).trim();
  const query = safeText(route.query?.guidance_seed_query).trim();
  const enabled =
    safeText(route.query?.guidance_seed).trim() === '1' ||
    [source, editor, reason, query].some(Boolean);

  return {
    enabled,
    source,
    editor: GUIDANCE_EDITOR_IDS.includes(editor) ? editor : '',
    reason,
    query,
  };
});
const guidanceSeedSummary = computed(() => {
  if (!guidanceSeedContext.value.enabled) return '';

  const parts = [];
  if (guidanceSeedContext.value.source) parts.push(`from ${guidanceSeedContext.value.source}`);
  if (guidanceSeedContext.value.editor === 'guardrails') parts.push('guardrails');
  if (guidanceSeedContext.value.editor === 'response_guidelines') parts.push('reply instructions');
  if (guidanceSeedContext.value.editor === 'communication_style') parts.push('reply style');
  if (guidanceSeedContext.value.editor === 'runtime_assembly') parts.push('runtime assembly');
  if (guidanceSeedContext.value.editor === 'scenarios') parts.push('scenarios');
  if (guidanceSeedContext.value.reason) parts.push(guidanceSeedContext.value.reason);
  return parts.join(' • ');
});
const guidanceSeedPrimaryActionLabel = computed(() => {
  switch (guidanceSeedContext.value.editor) {
    case 'guardrails':
      return 'Open guardrails';
    case 'response_guidelines':
      return 'Open reply instructions';
    case 'communication_style':
      return 'Open reply style';
    case 'runtime_assembly':
      return 'Open runtime assembly';
    case 'scenarios':
      return 'Open scenarios';
    default:
      return 'Open guidance area';
  }
});
const focusSeededGuidanceTarget = () => {
  if (!guidanceSeedContext.value.editor) return;
  openEditor(guidanceSeedContext.value.editor);
};
const clearGuidanceSeed = async () => {
  const nextQuery = { ...(route.query || {}) };
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  await router.replace({ query: nextQuery });
};
const retestSeededGuidanceContext = () => {
  const seededQuery = guidanceSeedContext.value.query;
  if (!seededQuery) return;

  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  GUIDANCE_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.content_seed;
  delete nextQuery.content_seed_source;
  delete nextQuery.content_seed_focus;
  delete nextQuery.content_seed_query;
  delete nextQuery.content_seed_reason;
  delete nextQuery.content_seed_scope_warning;
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  delete nextQuery.deploy_seed;
  delete nextQuery.deploy_seed_source;
  delete nextQuery.deploy_seed_focus;
  delete nextQuery.deploy_seed_reason;
  delete nextQuery.deploy_seed_inbox_id;
  delete nextQuery.escalation_seed;
  delete nextQuery.escalation_seed_source;
  delete nextQuery.escalation_seed_focus;
  delete nextQuery.escalation_seed_reason;
  delete nextQuery.escalation_seed_query;
  delete nextQuery.escalation_seed_label;
  nextQuery.test_query = seededQuery;
  nextQuery.test_reason = guidanceSeedContext.value.reason || 'guidance_seed';
  nextQuery.test_source = 'Guidance';
  router.replace({ query: nextQuery });
};

const openSettingsTab = () => {
  router.replace({ query: { ...(route.query || {}), tab: 'settings_general' } });
};

const addMalayGlossaryRow = () => {
  msShortFormGlossary.value = [
    ...(msShortFormGlossary.value || []),
    { short: '', meaning: '', note: '' },
  ];
  activeEditor.value = 'communication_style';
};

const removeMalayGlossaryRow = idx => {
  const next = [...(msShortFormGlossary.value || [])];
  next.splice(idx, 1);
  msShortFormGlossary.value = next;
};

const addThaiGlossaryRow = () => {
  thChatGlossary.value = [
    ...(thChatGlossary.value || []),
    { short: '', meaning: '', note: '' },
  ];
  activeEditor.value = 'communication_style';
};

const removeThaiGlossaryRow = idx => {
  const next = [...(thChatGlossary.value || [])];
  next.splice(idx, 1);
  thChatGlossary.value = next;
};

const fetchRuntimeConfig = async () => {
  if (!props.assistant?.id) return;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    useAlert(error?.message || 'Failed to load AI runtime guidance config');
  }
};

const fetchGuidanceMetrics = async () => {
  if (!props.assistant?.id || !accountId.value) {
    guidanceCardMetrics.value = blankGuidanceCardMetrics();
    guidanceScenarioMetrics.value = {};
    return;
  }

  guidanceMetricsLoading.value = true;
  try {
    const createdAfter = new Date(
      Date.now() - GUIDANCE_METRIC_WINDOW_DAYS * 24 * 60 * 60 * 1000
    ).toISOString();
    const baseParams = {
      account_id: accountId.value,
      assistant_id: props.assistant.id,
      decision_type_like: 'response.trace',
      created_after: createdAfter,
    };
    const [cardRes, scenarioRes] = await Promise.all([
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'guidance_card_outcomes',
          },
        }
      ),
      getWootAxios().get(
        `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`,
        {
          params: {
            ...baseParams,
            aggregate: 'guidance_scenario_outcomes',
            limit: 20,
          },
        }
      ),
    ]);
    const outcomeRows = normalizeGuidanceOutcomeRows(cardRes);
    const scenarioOutcomeRows = normalizeGuidanceOutcomeRows(scenarioRes);
    const next = blankGuidanceCardMetrics();

    GUIDANCE_METRIC_CARD_KEYS.forEach(key => {
      next[key] = outcomeRows[key] || emptyGuidanceCardMetric();
    });

    guidanceCardMetrics.value = next;
    guidanceScenarioMetrics.value = scenarioOutcomeRows;
  } catch (error) {
    guidanceCardMetrics.value = blankGuidanceCardMetrics();
    guidanceScenarioMetrics.value = {};
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to load guidance usage metrics'
    );
  } finally {
    guidanceMetricsLoading.value = false;
  }
};

const openAddScenario = () => {
  editingScenarioId.value = null;
  scenarioForm.title = '';
  scenarioForm.description = '';
  scenarioForm.instruction = '';
  scenarioForm.enabled = true;
  activeEditor.value = 'scenarios';
  markScenarioDraftSnapshot();
  showScenarioModal.value = true;
};

const openEditScenario = scenario => {
  editingScenarioId.value = scenario.id;
  scenarioForm.title = scenario.title;
  scenarioForm.description = scenario.description;
  scenarioForm.instruction = scenario.instruction;
  scenarioForm.enabled = scenario.enabled !== false;
  activeEditor.value = 'scenarios';
  markScenarioDraftSnapshot();
  showScenarioModal.value = true;
};

const closeScenarioModal = () => {
  showScenarioModal.value = false;
};

const reindexScenarios = () => {
  scenarios.forEach((scenario, idx) => {
    scenario.priority = idx + 1;
  });
};

const saveScenario = () => {
  if (!scenarioForm.title.trim()) {
    useAlert('Scenario title is required');
    return;
  }
  if (!scenarioForm.instruction.trim()) {
    useAlert('Scenario instruction is required');
    return;
  }

  if (editingScenarioId.value) {
    const idx = scenarios.findIndex(s => s.id === editingScenarioId.value);
    if (idx !== -1) {
      scenarios[idx] = normalizeScenario(
        {
          ...scenarios[idx],
          title: scenarioForm.title.trim(),
          description: scenarioForm.description.trim(),
          instruction: scenarioForm.instruction.trim(),
          enabled: scenarioForm.enabled,
          priority: scenarios[idx].priority,
        },
        idx
      );
    }
  } else {
    scenarios.push(
      normalizeScenario(
        {
          id: generateId(),
          title: scenarioForm.title.trim(),
          description: scenarioForm.description.trim(),
          instruction: scenarioForm.instruction.trim(),
          enabled: scenarioForm.enabled,
          priority: scenarios.length + 1,
        },
        scenarios.length
      )
    );
  }

  reindexScenarios();
  markScenarioDraftSnapshot();
  refreshDirtyState();
  closeScenarioModal();
};

const deleteScenario = id => {
  const idx = scenarios.findIndex(s => s.id === id);
  if (idx === -1) return;
  scenarios.splice(idx, 1);
  reindexScenarios();
  refreshDirtyState();
};

const moveScenario = (id, direction) => {
  const idx = scenarios.findIndex(s => s.id === id);
  if (idx === -1) return;
  const nextIdx = idx + direction;
  if (nextIdx < 0 || nextIdx >= scenarios.length) return;
  const current = scenarios[idx];
  scenarios[idx] = scenarios[nextIdx];
  scenarios[nextIdx] = current;
  reindexScenarios();
  refreshDirtyState();
};

const handleSave = async () => {
  if (canSaveScenarioDraft.value) {
    saveScenario();
  }
  if (!canPersistGuidance.value) return false;

  isSubmitting.value = true;
  try {
    const existingPromptSections = aiCloserConfig.value?.prompt_sections || {};
    const nextAssistantConfig = {
      ...(props.assistant?.config || {}),
      feature_citation: citationMode.value === 'auto',
    };
    const payload = {
      guardrails: persistedGuardrails.value,
      response_guidelines: persistedResponseGuidelines.value,
      answer_generation: {
        answer_length_strategy: answerLengthStrategy.value || 'balanced',
        directness_strategy: answerDirectnessStrategy.value || 'answer_first',
        sensitive_topics_strategy: sensitiveTopicStrategy.value || 'conservative',
        tone_enforcement_strategy: toneEnforcementStrategy.value || 'balanced',
        memory_depth_strategy: memoryDepthStrategy.value || 'standard',
        max_response_segments: normalizePositiveSegmentCount(maxResponseSegments.value),
        answer_confidence_strategy: answerConfidenceStrategy.value || 'off',
        outside_knowledge_strategy: outsideKnowledgeStrategy.value || 'standard',
        promise_safety_strategy: promiseSafetyStrategy.value || 'standard',
      },
      knowledge_retrieval: {
        ...((existingPromptSections || {}).knowledge_retrieval || {}),
        citation_mode: citationMode.value === 'auto' ? 'auto' : 'never',
      },
      language_style: {
        ...((existingPromptSections || {}).language_style || {}),
        ms_reply_form: malayReplyForm.value || 'auto',
        th_reply_form: thaiReplyForm.value || 'auto',
        ms_short_form_glossary: normalizeGlossaryRows(msShortFormGlossary.value),
        th_chat_glossary: normalizeGlossaryRows(thChatGlossary.value),
      },
      overview: safeText(overviewText.value),
      product_highlights: safeText(productHighlightsText.value),
      jobscope: safeText(jobscopeText.value),
      handling_flow: safeText(handlingFlowText.value),
      scenarios: scenarios.map((scenario, idx) => ({
        id: scenario.id || generateId(),
        title: safeText(scenario.title).trim(),
        description: safeText(scenario.description).trim(),
        instruction: safeText(scenario.instruction).trim(),
        enabled: scenario.enabled !== false,
        priority: idx + 1,
      })),
    };

    await store.dispatch('ailabAssistants/update', {
      id: props.assistant.id,
      assistant: {
        config: nextAssistantConfig,
      },
    });
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: props.assistant.id,
      data: {
        response_style: responseStyle.value || 'adaptive',
        prompt_sections: {
          ...existingPromptSections,
          ...payload,
        },
      },
    });
    await store.dispatch('ailabAssistants/show', props.assistant.id);

    markSavedSnapshot();
    useAlert('Guidance saved to AI runtime config');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to save guidance'
    );
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => aiCloserConfig.value,
  value => {
    if (props.isSavingAll) return;
    if (isDirty.value && !isSubmitting.value) return;
    loadFromConfig(value);
  },
  { immediate: true, deep: true }
);

watch(
  () => props.assistant?.config?.feature_citation,
  () => {
    if (props.isSavingAll) return;
    if (isDirty.value && !isSubmitting.value) return;
    citationMode.value = featureCitationEnabled(
      props.assistant?.config?.feature_citation
    )
      ? 'auto'
      : 'off';
  },
  { immediate: true }
);

watch(
  () => [
    route.query?.guidance_seed,
    route.query?.guidance_seed_editor,
    route.query?.guidance_seed_reason,
    route.query?.guidance_seed_query,
    route.query?.guidance_seed_source,
  ],
  () => {
    if (guidanceSeedContext.value.editor) {
      openEditor(guidanceSeedContext.value.editor);
    }
  },
  { immediate: true }
);

watch(
  () => props.assistant?.id,
  (nextAssistantId, previousAssistantId) => {
    if (!nextAssistantId) {
      guidanceCardMetrics.value = blankGuidanceCardMetrics();
      return;
    }
    if (nextAssistantId === previousAssistantId) return;
    fetchRuntimeConfig();
    fetchGuidanceMetrics();
  }
);

watch(
  [
    guardrailsText,
    responseGuidelinesText,
    responseStyle,
    malayReplyForm,
    thaiReplyForm,
    overviewText,
    productHighlightsText,
    jobscopeText,
    handlingFlowText,
    () => normalizeGlossaryRows(msShortFormGlossary.value),
    () => normalizeGlossaryRows(thChatGlossary.value),
    () => scenarios.map(scenario => ({
      id: scenario.id,
      title: scenario.title,
      description: scenario.description,
      instruction: scenario.instruction,
      enabled: scenario.enabled,
      priority: scenario.priority,
    })),
  ],
  () => {
    refreshDirtyState();
  },
  { deep: true }
);

onMounted(() => {
  fetchRuntimeConfig();
  fetchGuidanceMetrics();
});

defineExpose({
  save: handleSave,
  canSave: () => canSave.value,
});
</script>

<template>
  <div class="fin-ai flex flex-col h-full min-h-0">
    <div
      class="flex flex-col h-full min-h-0 overflow-hidden rounded-module bg-base-module shadow-lvl-0"
    >
      <div class="border-b border-neutral-border">
        <div class="mb-1">
          <div class="fin-page-header-row">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="btn o__secondary o__icon o__subtle"
                aria-label="Toggle sidebar"
              >
                <span class="i-lucide-panel-left-open size-4" />
              </button>
              <h1 class="heading o__size-1">Guidance</h1>
            </div>

            <div class="flex items-center space-x-2">
              <div class="guidance-owner-badge">Runtime owner</div>
              <button
                type="button"
                class="btn o__secondary"
                :disabled="!canSave"
                @click="handleSave"
              >
                {{
                  isSubmitting ? 'Saving...' : 'Save guidance'
                }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="fin-page-body">
        <div class="fin-page-hero fin-page-section">
          <div class="fin-page-hero__eyebrow">Customer-facing behavior</div>
          <div class="fin-page-hero__title">
            Set how the AI should answer, what it should avoid, and when it
            should follow a scenario.
          </div>
          <div class="fin-page-hero__copy">
            Start with reply instructions and scenarios. Only open advanced
            language controls when you need special Malay or Thai phrasing.
          </div>
          <div class="fin-page-stats">
            <div
              v-for="card in guidanceOverviewCards"
              :key="card.label"
              class="fin-page-stat"
            >
              <div class="fin-page-stat__label">{{ card.label }}</div>
              <div class="fin-page-stat__value">{{ card.value }}</div>
              <div class="fin-page-stat__note">{{ card.note }}</div>
            </div>
          </div>
        </div>

        <div
          v-if="guidanceSeedContext.enabled"
          class="guidance-upstream-card fin-page-section"
        >
          <div class="guidance-upstream-card__header">
            <div>
              <div class="fin-page-intro-title">Guidance seeded context</div>
              <div class="fin-page-intro-copy mt-1">
                {{ guidanceSeedSummary || 'A repair context was brought into Guidance.' }}
              </div>
              <div
                v-if="guidanceSeedContext.query"
                class="fin-page-intro-copy mt-1"
              >
                Sample query: {{ guidanceSeedContext.query }}
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                class="btn o__secondary"
                @click="focusSeededGuidanceTarget"
              >
                {{ guidanceSeedPrimaryActionLabel }}
              </button>
              <button
                v-if="guidanceSeedContext.query"
                type="button"
                class="btn o__secondary"
                @click="retestSeededGuidanceContext"
              >
                Retest sample
              </button>
              <button type="button" class="btn o__secondary" @click="clearGuidanceSeed">
                Clear seeded context
              </button>
            </div>
          </div>
        </div>

        <div class="guidance-upstream-card fin-page-section">
          <div class="guidance-upstream-card__header">
            <div>
              <div class="fin-page-intro-title">Brand identity still comes from Settings</div>
              <div class="fin-page-intro-copy mt-1">
                Name, voice, and preferred language stay in `Settings`. Guidance
                only controls reply behavior layered on top of that identity.
              </div>
            </div>
            <button type="button" class="btn o__secondary" @click="openSettingsTab">
              Open Settings
            </button>
          </div>
          <details class="fin-advanced-details mt-4">
            <summary>Show current identity fields</summary>
            <div class="fin-advanced-details__body">
              <div class="guidance-upstream-grid">
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">Nickname</div>
                  <div class="guidance-upstream-item__value">
                    {{ runtimeIdentitySummary.nickname }}
                  </div>
                </div>
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">Age</div>
                  <div class="guidance-upstream-item__value">{{ runtimeIdentitySummary.age }}</div>
                </div>
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">Gender</div>
                  <div class="guidance-upstream-item__value">
                    {{ runtimeIdentitySummary.gender }}
                  </div>
                </div>
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">Country</div>
                  <div class="guidance-upstream-item__value">
                    {{ runtimeIdentitySummary.country }}
                  </div>
                </div>
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">Preferred language</div>
                  <div class="guidance-upstream-item__value">
                    {{ runtimeIdentitySummary.preferredLanguage }}
                  </div>
                </div>
                <div class="guidance-upstream-item">
                  <div class="guidance-upstream-item__label">AI voice</div>
                  <div class="guidance-upstream-item__value">
                    {{ runtimeIdentitySummary.aiVoice }}
                  </div>
                </div>
              </div>
            </div>
          </details>
        </div>

        <div class="fin-page-section">
          <h3 class="text-h3 font-semibold mb-2">Choose what to adjust</h3>
          <p class="text-sm fin-muted">
            Keep the default view simple: start with guardrails, reply
            instructions, and scenarios before touching advanced controls.
          </p>

          <div class="mt-4 guidance-card-grid">
            <button
              v-for="card in guidanceCards"
              :key="card.id"
              type="button"
              class="guidance-entry-card"
              :class="{ 'is-active': activeEditor === card.id }"
              @click="openEditor(card.id)"
            >
              <div class="flex items-start justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="w-9 h-9 rounded-lg border border-neutral-border bg-neutral-container flex items-center justify-center"
                  >
                    <span :class="[card.icon, 'size-4']" />
                  </div>
                  <div class="min-w-0 text-left">
                    <div class="font-semibold">{{ card.title }}</div>
                    <div class="text-sm fin-muted">{{ card.description }}</div>
                    <div class="guidance-card-helper fin-muted">
                      {{
                        card.count
                          ? `${card.count} item${card.count === 1 ? '' : 's'} configured`
                          : 'Nothing configured yet'
                      }}
                    </div>
                  </div>
                </div>
                <span class="guidance-count-pill">{{ card.count }}</span>
              </div>
            </button>
          </div>
          <p class="mt-3 text-xs fin-muted guidance-metric-copy">
            Pick one area to edit. Usage metrics stay in the collapsed advanced
            summary below.
          </p>
        </div>

        <div v-if="isLoadingConfig" class="guidance-loading-state">
          <span class="i-lucide-loader-2 size-5 animate-spin" />
          <span>Loading guidance settings...</span>
        </div>

        <div v-else class="guidance-editor-stack">
          <section
            v-if="activeEditor === 'guardrails'"
            class="guidance-editor is-active"
          >
            <div class="guidance-editor__header">
              <div>
                <h3 class="text-h3 font-semibold">Guardrails</h3>
                <p class="text-sm fin-muted">
                  One rule per line. Use this for do-not-answer, compliance,
                  safety, and escalation boundaries.
                </p>
              </div>
              <div class="guidance-count-pill">{{ guardrailsCount }}</div>
            </div>
            <div class="guidance-runtime-grid guidance-runtime-grid--compact mb-4">
              <label class="guidance-field">
                <span class="guidance-field__label">When knowledge is missing</span>
                <select
                  v-model="outsideKnowledgeStrategy"
                  class="guidance-input"
                  @focus="openEditor('guardrails')"
                >
                  <option
                    v-for="option in OUTSIDE_KNOWLEDGE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Saves a real guardrail for how the AI should behave when it does
                  not have enough support from configured knowledge.
                </span>
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Promise safety</span>
                <select
                  v-model="promiseSafetyStrategy"
                  class="guidance-input"
                  @focus="openEditor('guardrails')"
                >
                  <option
                    v-for="option in PROMISE_SAFETY_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Tightens the runtime rule for refunds, approvals, timelines, and
                  other outcome-sensitive claims.
                </span>
              </label>
            </div>
            <div class="guidance-owner-callout mb-4">
              <div>
                <div class="guidance-owner-callout__title">
                  Structured safety controls also save into the real runtime
                </div>
                <div class="guidance-owner-callout__copy">
                  These controls are stored alongside the freeform guardrails that
                  the prompt builder already consumes today.
                </div>
              </div>
            </div>
            <textarea
              v-model="guardrailsText"
              class="guidance-textarea"
              rows="8"
              placeholder="Example: Never promise guaranteed outcomes."
              @focus="openEditor('guardrails')"
            />
          </section>

          <section
            v-if="activeEditor === 'response_guidelines'"
            class="guidance-editor is-active"
          >
            <div class="guidance-editor__header">
              <div>
                <h3 class="text-h3 font-semibold">Reply instructions</h3>
                <p class="text-sm fin-muted">
                  One instruction per line. Use this for structure, clarity, and
                  how replies should read.
                </p>
              </div>
              <div class="guidance-count-pill">
                {{ responseGuidelinesCount }}
              </div>
            </div>
            <div class="guidance-runtime-grid guidance-runtime-grid--compact mb-4">
              <label class="guidance-field">
                <span class="guidance-field__label">Answer length</span>
                <select
                  v-model="answerLengthStrategy"
                  class="guidance-input"
                  @focus="openEditor('response_guidelines')"
                >
                  <option
                    v-for="option in ANSWER_LENGTH_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Writes a runtime instruction for how long replies should feel by
                  default.
                </span>
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Directness</span>
                <select
                  v-model="answerDirectnessStrategy"
                  class="guidance-input"
                  @focus="openEditor('response_guidelines')"
                >
                  <option
                    v-for="option in ANSWER_DIRECTNESS_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Choose whether the AI answers first, clarifies first, or guides
                  step by step.
                </span>
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Sensitive topics</span>
                <select
                  v-model="sensitiveTopicStrategy"
                  class="guidance-input"
                  @focus="openEditor('response_guidelines')"
                >
                  <option
                    v-for="option in SENSITIVE_TOPIC_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Tighten wording for refunds, payment risk, compliance, and other
                  higher-risk topics.
                </span>
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Tone enforcement</span>
                <select
                  v-model="toneEnforcementStrategy"
                  class="guidance-input"
                  @focus="openEditor('response_guidelines')"
                >
                  <option
                    v-for="option in TONE_ENFORCEMENT_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Controls how tightly the runtime should stay aligned to the saved
                  voice and phrasing style.
                </span>
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Citation mode</span>
                <select
                  v-model="citationMode"
                  class="guidance-input"
                  @focus="openEditor('response_guidelines')"
                >
                  <option
                    v-for="option in CITATION_MODE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  Controls the real assistant citation feature flag used when links
                  and media sources are attached to replies.
                </span>
              </label>
            </div>
            <div class="guidance-owner-callout mb-4">
              <div>
                <div class="guidance-owner-callout__title">
                  Structured answer strategy now saves into the real runtime
                </div>
                <div class="guidance-owner-callout__copy">
                  These controls feed the same AI runtime that already consumes
                  reply instructions, instead of creating a separate hidden config
                  path.
                </div>
              </div>
            </div>
            <details class="fin-advanced-details guidance-advanced-panel mb-4">
              <summary>
                Advanced answer controls
                <span class="guidance-advanced-summary-note">
                  Memory, bubble limit, and confidence gating
                </span>
              </summary>
              <div class="fin-advanced-details__body">
                <div class="guidance-runtime-grid guidance-runtime-grid--compact">
                  <label class="guidance-field">
                    <span class="guidance-field__label">Conversation memory depth</span>
                    <select
                      v-model="memoryDepthStrategy"
                      class="guidance-input"
                      @focus="openEditor('response_guidelines')"
                    >
                      <option
                        v-for="option in MEMORY_DEPTH_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <span class="guidance-field__help">
                      Controls how much recent conversation context the runtime reads
                      before answering.
                    </span>
                  </label>

                  <label class="guidance-field">
                    <span class="guidance-field__label">Maximum response bubbles</span>
                    <select
                      v-model="maxResponseSegments"
                      class="guidance-input"
                      @focus="openEditor('response_guidelines')"
                    >
                      <option
                        v-for="option in MAX_RESPONSE_SEGMENT_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <span class="guidance-field__help">
                      Enables real runtime multi-bubble replies when set above one.
                    </span>
                  </label>

                  <label class="guidance-field">
                    <span class="guidance-field__label">Auto-reply confidence</span>
                    <select
                      v-model="answerConfidenceStrategy"
                      class="guidance-input"
                      @focus="openEditor('response_guidelines')"
                    >
                      <option
                        v-for="option in ANSWER_CONFIDENCE_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <span class="guidance-field__help">
                      Lets the runtime downgrade weak AI answers to suggestion mode
                      instead of auto-sending them.
                    </span>
                  </label>
                </div>
              </div>
            </details>
            <textarea
              v-model="responseGuidelinesText"
              class="guidance-textarea"
              rows="8"
              placeholder="Example: Start with the direct answer, then explain briefly."
              @focus="openEditor('response_guidelines')"
            />
          </section>

          <section
            v-if="activeEditor === 'communication_style'"
            class="guidance-editor is-active"
          >
            <div class="guidance-editor__header">
              <div>
                <h3 class="text-h3 font-semibold">Reply style</h3>
                <p class="text-sm fin-muted">
                  Pick the overall tone first. Keep language-specific controls
                  collapsed unless you need special local phrasing rules.
                </p>
              </div>
              <div class="guidance-count-pill">{{ communicationStyleCount }}</div>
            </div>

            <div class="guidance-runtime-grid guidance-runtime-grid--compact">
              <label class="guidance-field">
                <span class="guidance-field__label">Response style</span>
                <select
                  v-model="responseStyle"
                  class="guidance-input"
                  @focus="openEditor('communication_style')"
                >
                  <option
                    v-for="option in RESPONSE_STYLE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <span class="guidance-field__help">
                  This is the main tone customers will feel in everyday replies.
                </span>
              </label>
            </div>

            <details class="fin-advanced-details guidance-advanced-panel">
              <summary>
                Advanced language controls
                <span class="guidance-advanced-summary-note">
                  {{ advancedCommunicationStyleCount }} active
                </span>
              </summary>
              <div class="fin-advanced-details__body">
                <div class="guidance-owner-callout">
                  <div>
                    <div class="guidance-owner-callout__title">
                      Brand identity from Settings still affects replies
                    </div>
                    <div class="guidance-owner-callout__copy">
                      Nickname <strong>{{ runtimeIdentitySummary.nickname }}</strong>,
                      preferred language
                      <strong>{{ runtimeIdentitySummary.preferredLanguage }}</strong>,
                      and voice <strong>{{ runtimeIdentitySummary.aiVoice }}</strong>
                      still come from `Settings`.
                    </div>
                  </div>
                  <button type="button" class="btn o__secondary" @click="openSettingsTab">
                    Open Settings
                  </button>
                </div>

                <div class="guidance-runtime-grid">
                  <label class="guidance-field">
                    <span class="guidance-field__label">Malay reply form</span>
                    <select
                      v-model="malayReplyForm"
                      class="guidance-input"
                      @focus="openEditor('communication_style')"
                    >
                      <option value="auto">Auto</option>
                      <option value="short">Short</option>
                      <option value="standard">Standard</option>
                    </select>
                    <span class="guidance-field__help">
                      Use this only when you need a more specific Malay reply style.
                    </span>
                  </label>

                  <label class="guidance-field">
                    <span class="guidance-field__label">Thai reply form</span>
                    <select
                      v-model="thaiReplyForm"
                      class="guidance-input"
                      @focus="openEditor('communication_style')"
                    >
                      <option value="auto">Auto</option>
                      <option value="chat">Chat</option>
                      <option value="standard">Standard</option>
                    </select>
                    <span class="guidance-field__help">
                      Use this only when Thai replies need a specific format.
                    </span>
                  </label>
                </div>

                <div class="guidance-language-style-stack">
                  <div class="guidance-language-style-card">
                    <div class="guidance-language-style-card__header">
                      <div>
                        <h4 class="font-semibold">Malay short-form glossary</h4>
                        <p class="text-sm fin-muted">
                          Optional glossary for brand-specific short forms.
                        </p>
                      </div>
                      <div class="guidance-actions guidance-actions--start">
                        <button type="button" class="btn o__secondary" @click="addMalayGlossaryRow">
                          Add row
                        </button>
                        <button
                          type="button"
                          class="btn o__secondary"
                          @click="msShortFormGlossary = defaultMalayGlossary()"
                        >
                          Reset defaults
                        </button>
                      </div>
                    </div>

                    <div v-if="!msShortFormGlossary.length" class="guidance-empty-state">
                      No Malay glossary rows yet.
                    </div>
                    <div v-else class="guidance-glossary-list">
                      <div
                        v-for="(row, idx) in msShortFormGlossary"
                        :key="`ms-${idx}-${row.short}`"
                        class="guidance-glossary-row"
                      >
                        <input
                          v-model="row.short"
                          class="guidance-input"
                          placeholder="Short form"
                          @focus="openEditor('communication_style')"
                        />
                        <input
                          v-model="row.meaning"
                          class="guidance-input"
                          placeholder="Meaning"
                          @focus="openEditor('communication_style')"
                        />
                        <input
                          v-model="row.note"
                          class="guidance-input"
                          placeholder="Note"
                          @focus="openEditor('communication_style')"
                        />
                        <button
                          type="button"
                          class="btn o__secondary"
                          @click="removeMalayGlossaryRow(idx)"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="guidance-language-style-card">
                    <div class="guidance-language-style-card__header">
                      <div>
                        <h4 class="font-semibold">Thai chat glossary</h4>
                        <p class="text-sm fin-muted">
                          Optional glossary for Thai short forms and casual phrasing.
                        </p>
                      </div>
                      <div class="guidance-actions guidance-actions--start">
                        <button type="button" class="btn o__secondary" @click="addThaiGlossaryRow">
                          Add row
                        </button>
                        <button
                          type="button"
                          class="btn o__secondary"
                          @click="thChatGlossary = defaultThaiChatGlossary()"
                        >
                          Reset defaults
                        </button>
                      </div>
                    </div>

                    <div v-if="!thChatGlossary.length" class="guidance-empty-state">
                      No Thai glossary rows yet.
                    </div>
                    <div v-else class="guidance-glossary-list">
                      <div
                        v-for="(row, idx) in thChatGlossary"
                        :key="`th-${idx}-${row.short}`"
                        class="guidance-glossary-row"
                      >
                        <input
                          v-model="row.short"
                          class="guidance-input"
                          placeholder="Chat form"
                          @focus="openEditor('communication_style')"
                        />
                        <input
                          v-model="row.meaning"
                          class="guidance-input"
                          placeholder="Meaning"
                          @focus="openEditor('communication_style')"
                        />
                        <input
                          v-model="row.note"
                          class="guidance-input"
                          placeholder="Note"
                          @focus="openEditor('communication_style')"
                        />
                        <button
                          type="button"
                          class="btn o__secondary"
                          @click="removeThaiGlossaryRow(idx)"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </section>

          <section
            v-if="activeEditor === 'runtime_assembly'"
            class="guidance-editor is-active"
          >
            <div class="guidance-editor__header">
              <div>
                <h3 class="text-h3 font-semibold">Business context</h3>
                <p class="text-sm fin-muted">
                  Give the AI the brand context it should consistently carry into
                  customer replies.
                </p>
              </div>
              <div class="guidance-count-pill">{{ runtimeAssemblyCount }}</div>
            </div>

            <div class="guidance-runtime-grid">
              <label class="guidance-field">
                <span class="guidance-field__label">Overview</span>
                <textarea
                  v-model="overviewText"
                  class="guidance-textarea"
                  rows="5"
                  placeholder="What should the AI know about the brand, offer, or business at a glance?"
                  @focus="openEditor('runtime_assembly')"
                />
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Product highlights</span>
                <textarea
                  v-model="productHighlightsText"
                  class="guidance-textarea"
                  rows="5"
                  placeholder="Key selling points, differentiators, and product strengths."
                  @focus="openEditor('runtime_assembly')"
                />
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Job scope</span>
                <textarea
                  v-model="jobscopeText"
                  class="guidance-textarea"
                  rows="5"
                  placeholder="What role should the AI play and what should it focus on?"
                  @focus="openEditor('runtime_assembly')"
                />
              </label>

              <label class="guidance-field">
                <span class="guidance-field__label">Handling flow</span>
                <textarea
                  v-model="handlingFlowText"
                  class="guidance-textarea"
                  rows="6"
                  placeholder="Preferred step-by-step handling flow for common conversations."
                  @focus="openEditor('runtime_assembly')"
                />
              </label>
            </div>
          </section>

          <section
            v-if="activeEditor === 'scenarios'"
            class="guidance-editor is-active"
          >
            <div class="guidance-editor__header">
              <div>
                <h3 class="text-h3 font-semibold">Scenarios</h3>
                <p class="text-sm fin-muted">
                  Add reusable if/then instructions for recurring situations.
                </p>
              </div>
              <div class="flex items-center gap-2">
                <div class="guidance-count-pill">{{ scenariosCount }}</div>
                <button
                  type="button"
                  class="btn o__secondary"
                  @click="openAddScenario"
                >
                  <span class="i-lucide-plus size-4" />
                  Add scenario
                </button>
              </div>
            </div>

            <div v-if="!scenarios.length" class="guidance-empty-state">
              No scenarios yet.
            </div>

            <div v-else class="guidance-scenario-list">
              <div
                v-for="scenario in scenarios"
                :key="scenario.id"
                class="guidance-scenario-item"
              >
                <div class="guidance-scenario-main">
                  <div class="guidance-scenario-row">
                    <span class="guidance-scenario-priority">
                      #{{ scenario.priority }}
                    </span>
                    <span class="font-semibold">{{ scenario.title }}</span>
                    <span
                      class="guidance-status-pill"
                      :class="scenario.enabled ? 'is-active' : 'is-inactive'"
                    >
                      {{ scenario.enabled ? 'Active' : 'Inactive' }}
                    </span>
                  </div>
                  <div
                    v-if="scenario.description"
                    class="text-sm fin-muted mt-1"
                  >
                    {{ scenario.description }}
                  </div>
                  <div class="guidance-scenario-instruction">
                    {{ scenario.instruction }}
                  </div>
                </div>
                <div class="guidance-scenario-actions">
                  <button
                    type="button"
                    class="guidance-icon-btn"
                    :disabled="scenario.priority === 1"
                    @click="moveScenario(scenario.id, -1)"
                  >
                    <span class="i-lucide-arrow-up size-4" />
                  </button>
                  <button
                    type="button"
                    class="guidance-icon-btn"
                    :disabled="scenario.priority === scenarios.length"
                    @click="moveScenario(scenario.id, 1)"
                  >
                    <span class="i-lucide-arrow-down size-4" />
                  </button>
                  <button
                    type="button"
                    class="guidance-icon-btn"
                    @click="openEditScenario(scenario)"
                  >
                    <span class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    type="button"
                    class="guidance-icon-btn is-danger"
                    @click="deleteScenario(scenario.id)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          <div class="guidance-actions">
            <button
              type="button"
              class="btn o__secondary"
              :disabled="!canSave"
              @click="handleSave"
            >
              {{ isSubmitting ? 'Saving...' : 'Save guidance' }}
            </button>
          </div>
        </div>

        <details class="fin-advanced-details mt-8">
          <summary>Internal metrics and status</summary>
          <div class="fin-advanced-details__body">
            <div class="fin-table">
              <table>
              <thead>
                <tr>
                  <th style="width: 34%">Card</th>
                  <th style="width: 12%">Type</th>
                  <th style="width: 14%">Status</th>
                  <th style="width: 20%">Metrics</th>
                  <th style="width: 20%">Owner</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!guidanceItems.length">
                  <td class="fin-muted">No guidance cards yet</td>
                  <td class="fin-muted">—</td>
                  <td class="fin-muted">—</td>
                  <td class="fin-muted">—</td>
                  <td class="fin-muted">—</td>
                </tr>
                <tr
                  v-for="item in guidanceItems"
                  :key="item.id"
                  class="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  @click="
                    item.id === 'runtime_identity_inputs'
                      ? openSettingsTab()
                      : openEditor(item.type === 'Scenario' ? 'scenarios' : item.id)
                  "
                >
                  <td class="font-medium">{{ item.title }}</td>
                  <td>{{ item.type }}</td>
                  <td>
                    <span class="inline-flex items-center gap-1.5">
                      <span
                        class="w-2 h-2 rounded-full"
                        :class="
                          item.status === 'Active'
                            ? 'bg-green-500'
                            : item.status === 'Inactive'
                              ? 'bg-amber-500'
                              : 'bg-neutral-400'
                        "
                      ></span>
                      {{ item.status }}
                    </span>
                  </td>
                  <td>
                    <div
                      v-if="item.type === 'Scenario' && item.metricKey"
                      class="guidance-metric-copy fin-muted"
                    >
                      <div class="guidance-table-metrics">
                        <span
                          v-for="metric in guidanceMetricPills(item.metricKey)"
                          :key="`${item.id}-${metric.label}`"
                          class="guidance-metric-pill"
                        >
                          {{ metric.label }} {{ metric.value }}
                        </span>
                      </div>
                    </div>
                    <div v-else class="guidance-table-metrics">
                      <span
                        v-for="metric in guidanceMetricPills(item.id)"
                        :key="`${item.id}-${metric.label}`"
                        class="guidance-metric-pill"
                      >
                        {{ metric.label }} {{ metric.value }}
                      </span>
                    </div>
                    <div class="guidance-metric-copy fin-muted">
                      {{
                        guidanceMetricRateCopy(
                          item.type === 'Scenario' && item.metricKey ? item.metricKey : item.id
                        )
                      }}
                    </div>
                  </td>
                  <td class="fin-muted">{{ item.lastUpdated }}</td>
                </tr>
              </tbody>
              </table>
            </div>
          </div>
        </details>
      </div>
    </div>
  </div>

  <div
    v-if="showScenarioModal"
    class="guidance-modal__backdrop"
    @click.self="closeScenarioModal"
  >
    <div class="guidance-modal">
      <div class="guidance-modal__header">
        <h3 class="text-h3 font-semibold">
          {{ editingScenarioId ? 'Edit scenario' : 'Add scenario' }}
        </h3>
        <button
          type="button"
          class="guidance-icon-btn"
          @click="closeScenarioModal"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <div class="guidance-modal__body">
        <label class="guidance-field">
          <span class="guidance-field__label">Title</span>
          <input
            v-model="scenarioForm.title"
            type="text"
            class="guidance-input"
            placeholder="e.g. Price objection"
          />
        </label>

        <label class="guidance-field">
          <span class="guidance-field__label">Description</span>
          <textarea
            v-model="scenarioForm.description"
            class="guidance-textarea guidance-textarea--compact"
            rows="3"
            placeholder="Optional context shown to editors."
          />
        </label>

        <label class="guidance-field">
          <span class="guidance-field__label">Instruction</span>
          <textarea
            v-model="scenarioForm.instruction"
            class="guidance-textarea guidance-textarea--compact"
            rows="5"
            placeholder="What should the AI do in this situation?"
          />
        </label>

        <label class="guidance-checkbox">
          <input v-model="scenarioForm.enabled" type="checkbox" />
          <span>Enabled</span>
        </label>
      </div>

      <div class="guidance-modal__footer">
        <button type="button" class="btn o__secondary" @click="closeScenarioModal">
          Cancel
        </button>
        <button
          type="button"
          class="btn o__secondary"
          :disabled="!canSaveScenarioDraft"
          @click="saveScenario"
        >
          Save scenario
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.guidance-owner-badge {
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 9999px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
}

.guidance-intro-card {
  border: 1px solid var(--fin-neutral-border);
  background: #fafaf9;
  border-radius: 14px;
  padding: 14px;
}

.guidance-upstream-card {
  border: 1px solid var(--fin-neutral-border);
  background: #ffffff;
  border-radius: 18px;
  padding: 18px;
}

.guidance-upstream-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.guidance-upstream-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.guidance-upstream-item {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 12px;
}

.guidance-upstream-item__label {
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #64748b;
}

.guidance-upstream-item__value {
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #111827;
  word-break: break-word;
}

.guidance-inline-code {
  background: #f3f4f6;
  border-radius: 6px;
  padding: 1px 6px;
}

.guidance-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px;
}

.guidance-entry-card {
  width: 100%;
  border: 1px solid var(--fin-neutral-border);
  border-radius: 14px;
  padding: 14px;
  background: #ffffff;
  cursor: pointer;
  transition: box-shadow 120ms ease, border-color 120ms ease;
}

.guidance-entry-card:hover,
.guidance-entry-card.is-active {
  box-shadow: var(--fin-shadow-0);
  border-color: #bfdbfe;
  background: #fdfefe;
}

.guidance-count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 9999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 12px;
  font-weight: 700;
  padding: 0 8px;
}

.guidance-metric-pill-row,
.guidance-table-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.guidance-metric-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 9999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.guidance-metric-copy {
  line-height: 1.5;
}

.guidance-card-helper {
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
}

.guidance-loading-state {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
  font-size: 13px;
  padding: 12px 2px 20px;
}

.guidance-editor-stack {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.guidance-editor {
  border: 1px solid var(--fin-neutral-border);
  border-radius: 18px;
  background: #ffffff;
  padding: 22px;
}

.guidance-editor.is-active {
  border-color: #cbd5e1;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
}

.guidance-editor__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.guidance-language-style-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 18px;
}

.guidance-advanced-panel {
  margin-top: 16px;
}

.guidance-advanced-summary-note {
  margin-left: auto;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.guidance-owner-callout {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 14px;
  background: #f8fafc;
}

.guidance-owner-callout__title {
  font-size: 13px;
  line-height: 18px;
  font-weight: 700;
  color: #111827;
}

.guidance-owner-callout__copy {
  margin-top: 4px;
  font-size: 12px;
  line-height: 18px;
  color: #475569;
}

.guidance-language-style-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  background: #fafafa;
}

.guidance-language-style-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.guidance-runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.guidance-runtime-grid--compact {
  grid-template-columns: minmax(0, 420px);
}

.guidance-textarea,
.guidance-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  line-height: 20px;
  padding: 12px 14px;
}

.guidance-textarea {
  resize: vertical;
}

.guidance-textarea:focus,
.guidance-input:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12);
}

.guidance-textarea--compact {
  min-height: 0;
}

.guidance-empty-state {
  border: 1px dashed #d1d5db;
  border-radius: 14px;
  padding: 18px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.guidance-scenario-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.guidance-scenario-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  background: #ffffff;
}

.guidance-scenario-main {
  flex: 1;
  min-width: 0;
}

.guidance-scenario-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.guidance-scenario-priority {
  font-size: 12px;
  line-height: 16px;
  color: #6b7280;
  font-weight: 700;
}

.guidance-status-pill {
  border-radius: 9999px;
  padding: 3px 8px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
}

.guidance-status-pill.is-active {
  background: #dcfce7;
  color: #166534;
}

.guidance-status-pill.is-inactive {
  background: #fef3c7;
  color: #92400e;
}

.guidance-scenario-instruction {
  margin-top: 10px;
  font-size: 13px;
  line-height: 19px;
  color: #111827;
  white-space: pre-wrap;
}

.guidance-scenario-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.guidance-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
}

.guidance-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.guidance-icon-btn.is-danger {
  color: #b91c1c;
}

.guidance-actions {
  display: flex;
  justify-content: flex-end;
}

.guidance-actions--start {
  justify-content: flex-start;
  margin-top: 0;
}

.guidance-glossary-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.guidance-glossary-row {
  display: grid;
  grid-template-columns: 1.1fr 1.3fr 1.3fr auto;
  gap: 10px;
  align-items: center;
}

.guidance-modal__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 50;
}

.guidance-modal {
  width: min(640px, 100%);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.guidance-modal__header,
.guidance-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
}

.guidance-modal__footer {
  border-bottom: 0;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
}

.guidance-modal__body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.guidance-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.guidance-field__label {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #111827;
}

.guidance-field__help {
  font-size: 12px;
  line-height: 17px;
  color: #6b7280;
}

.guidance-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 18px;
  color: #111827;
}

@media (max-width: 1100px) {
  .guidance-upstream-grid,
  .guidance-glossary-row {
    grid-template-columns: 1fr;
  }

  .guidance-owner-callout,
  .guidance-upstream-card__header,
  .guidance-language-style-card__header {
    flex-direction: column;
  }

  .guidance-runtime-grid--compact {
    grid-template-columns: 1fr;
  }
}
</style>

